import Delivery from '@redbubble/delivery-dates';
import {
  Box,
  constants,
  ListContent,
  ListItem,
  ListMedia,
  Skeleton,
  Text,
  TextLink,
} from '@redbubble/design-system';
import {
  defineMessages,
  FormattedMessage,
  useIntl,
} from '@redbubble/next/intl';
import Image from 'next/legacy/image';
import { DeliveryDates } from '../../../../types/shop-bff/types';
import styles from './ProductTrustSignals.module.scss';
import { getFlagSvgFromPublicAssets } from './utils';

const getLocalisedGiftCardLink = (locale) => {
  if (locale === 'en') {
    return 'https://redbubble.com/gift-certificates/new';
  }

  return `https://redbubble.com/${locale}/gift-certificates/new`;
};

export const messages = defineMessages({
  delivery: {
    defaultMessage: 'Delivery',
  },
  deliversBy: {
    defaultMessage: 'Delivers by {targetDate}',
  },
  express: {
    defaultMessage: 'Express',
  },
  expressBy: {
    defaultMessage: 'Express by {date}',
  },
  standard: {
    defaultMessage: 'Standard',
  },
  standardBetween: {
    defaultMessage: `Standard between {dateRange}`,
  },
  lastChanceToday: {
    defaultMessage: 'Order by 11:59PM ({shippingType})',
  },
  daysLeft: {
    defaultMessage: '{daysLeftToOrder} days left ({shippingType})',
  },
  flagOf: {
    defaultMessage: 'Flag of {countryCode}',
  },
  notSoonEnough: {
    defaultMessage: 'Not soon enough?',
  },
  giveGiftCard: {
    defaultMessage: 'Give a digital gift card.',
  },
});

interface DeliveryTrustSignalProps {
  deliveryDates: DeliveryDates;
  countryCode: string;
  loading: boolean;
  locale: string;
}

export const DeliveryTrustSignal = ({
  deliveryDates,
  countryCode,
  loading,
  locale,
}: DeliveryTrustSignalProps) => {
  const intl = useIntl();

  // the values need to match the shape of the Delivery component props
  // https://github.com/redbubble/js-packages/blob/2babbd08ec69ec419f98ba3e4b89ca8660dd1238/packages/delivery-dates/src/DeliveryDates.js#L214-L224
  const deliveryTranslatedStr = {
    daysLeft: intl.formatMessage(messages.daysLeft, {
      daysLeftToOrder: '%{daysLeftToOrder}',
      shippingType: '%{shippingType}',
    }),
    deliversBy: intl.formatMessage(messages.deliversBy, {
      targetDate: '%{targetDate}',
    }),
    delivery: intl.formatMessage(messages.delivery),
    express: intl.formatMessage(messages.express, {
      date: '%{date}',
    }),
    expressBy: intl.formatMessage(messages.expressBy, {
      date: '%{date}',
    }),
    flagOf: intl.formatMessage(messages.flagOf, {
      countryCode: '%{countryCode}',
    }),
    standard: intl.formatMessage(messages.standard),
    standardBetween: intl.formatMessage(messages.standardBetween, {
      dateRange: '%{dateRange}',
    }),
    lastChanceToday: intl.formatMessage(messages.lastChanceToday, {
      shippingType: '%{shippingType}',
    }),
  };

  if (loading) {
    return (
      <ListItem data-testid="delivery-trust-signal-skeleton">
        <ListMedia>
          <Skeleton height={28} width={28} animated roundedCorners />
        </ListMedia>
        <ListContent className={styles.skeletonContentWrapper}>
          <Skeleton height={26} width={100} animated roundedCorners />
          <Skeleton height={26} width={150} animated roundedCorners />
          <Skeleton height={26} width={200} animated roundedCorners />
        </ListContent>
      </ListItem>
    );
  }

  if (!deliveryDates) return null;

  const flagSrc = countryCode ? getFlagSvgFromPublicAssets(countryCode) : '';

  return (
    <ListItem className={styles.deliveryTrustSignalWrapper}>
      <Delivery
        countryCode={countryCode}
        displayCentered={false}
        strings={deliveryTranslatedStr}
        dates={deliveryDates}
        flagSrc={flagSrc}
      >
        {({ flag, lines, title }) => (
          <>
            <ListMedia>
              <Image src={flag.src} alt={flag.alt} width={28} height={28} />
            </ListMedia>
            <ListContent>
              <Text type="display5" display="block">
                {title}
              </Text>
              {lines.map((line: string) => {
                if (line.includes('<strong>')) {
                  const [beforeStrong, betweenStrong, afterStrong] = line
                    .replace(/<[/strong>]*>/g, ',')
                    .split(',');

                  return (
                    <Text key={line} type="body2" display="block">
                      {beforeStrong}
                      <strong>{betweenStrong}</strong>
                      {afterStrong}
                    </Text>
                  );
                }
                return (
                  <Text key={line} type="body2" display="block">
                    {line}
                  </Text>
                );
              })}
              <Box paddingTop="xs">
                <Text type="body2">
                  {intl.formatMessage(messages.notSoonEnough)}
                  <TextLink
                    intent={constants.PRIMARY}
                    href={getLocalisedGiftCardLink(locale)}
                    type="body2"
                    target="_blank"
                  >
                    &nbsp;{intl.formatMessage(messages.giveGiftCard)}
                    <span className="clipped">
                      <FormattedMessage defaultMessage=" - opens in new window or tab" />
                    </span>
                  </TextLink>
                </Text>
              </Box>
            </ListContent>
          </>
        )}
      </Delivery>
    </ListItem>
  );
};
